import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  TopupcardCategoryState,
  TopupcardCategoryResponse,
  TopupcardCategory,
} from "./types/topup-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: TopupcardCategoryState = {
  topupcardCategories: [],
  topupcardCategory: null,
  selectedTopupcardCategory: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE TopupCategory (supports FormData for image upload)
export const createTopupcardCategory = createAsyncThunk<
  TopupcardCategoryResponse,
  FormData | Omit<TopupcardCategory, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("topupcardCategories/create", async (dataCategoryData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = dataCategoryData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/top-ups-categories`,
      dataCategoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      dataCategory: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create top up category" },
    );
  }
});

// READ (fetch all) TopupCategories (with pagination)
export const fetchTopupcardCategories = createAsyncThunk<
  TopupcardCategoryResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("topupcardCategories/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 30,
      search,
      status,
      category,
      country,
      sort_by,
      sort_order,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/top-ups-categories`, {
        params,
        headers,
      });
    }
    return {
      message: res.data.data.message,
      topupcardCategories: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch top up categories" },
    );
  }
});

// READ (fetch one) TopupCategory by ID
export const fetchTopupcardCategoryById = createAsyncThunk<
  TopupcardCategory,
  number,
  { rejectValue: ApiError; state: RootState }
>("topupcardCategories/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/top-ups-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch top up category" },
    );
  }
});

// UPDATE TopupCategory (supports FormData for image upload)
export const updateTopupcardCategory = createAsyncThunk<
  TopupcardCategory,
  { id: number; data: Partial<TopupcardCategory> | FormData },
  { rejectValue: ApiError; state: RootState }
>("topupcardCategories/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/top-ups-categories/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update top up category" },
    );
  }
});

// DELETE TopupCategory
export const deleteTopupcardCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("topupcardCategories/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/top-ups-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete top up category" },
    );
  }
});

// Slice
const topupcardCategorySlice = createSlice({
  name: "topupcardCategories",
  initialState,
  reducers: {
    resetTopupcardCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedTopupcardCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createTopupcardCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createTopupcardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.topupcardCategory) {
          state.topupcardCategories.unshift(action.payload.topupcardCategory);
        }
      })
      .addCase(createTopupcardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create top up category";
      })

      // Fetch All
      .addCase(fetchTopupcardCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopupcardCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.topupcardCategories = action.payload.topupcardCategories || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchTopupcardCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch top up categories";
      })

      // Fetch One
      .addCase(fetchTopupcardCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedTopupcardCategory = null;
      })
      .addCase(fetchTopupcardCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTopupcardCategory = action.payload;
      })
      .addCase(fetchTopupcardCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch top up category";
      })

      // Update
      .addCase(updateTopupcardCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateTopupcardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.topupcardCategories = state.topupcardCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category,
        );
        if (state.selectedTopupcardCategory?.id === action.payload.id) {
          state.selectedTopupcardCategory = action.payload;
        }
      })
      .addCase(updateTopupcardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update top up category";
      })

      // Delete
      .addCase(deleteTopupcardCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteTopupcardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.topupcardCategories = state.topupcardCategories.filter(
          (category) => category.id !== action.payload,
        );
      })
      .addCase(deleteTopupcardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete top up card category";
      });
  },
});

export const { resetTopupcardCategoryState } = topupcardCategorySlice.actions;
export default topupcardCategorySlice.reducer;
